import { ETHGame } from "./game";

// Password verification class
class PasswordProtection {
  private readonly CORRECT_PASSWORD = "qwer1234"; // Set your password here
  private readonly LOGIN_EXPIRY_KEY = "flyline_login_expiry";
  private readonly LOGIN_STATUS_KEY = "flyline_login_status";
  private readonly LOGIN_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  private passwordOverlay: HTMLElement | null = null;
  private gameContainer: HTMLElement | null = null;
  private passwordInput: HTMLInputElement | null = null;
  private passwordSubmit: HTMLButtonElement | null = null;
  private passwordError: HTMLElement | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    this.passwordOverlay = document.getElementById("passwordOverlay");
    this.gameContainer = document.getElementById("gameContainer");
    this.passwordInput = document.getElementById(
      "passwordInput"
    ) as HTMLInputElement;
    this.passwordSubmit = document.getElementById(
      "passwordSubmit"
    ) as HTMLButtonElement;
    this.passwordError = document.getElementById("passwordError");

    // Check if user is already logged in
    if (this.isLoggedIn()) {
      this.authenticate();
    } else if (this.passwordInput && this.passwordSubmit) {
      this.bindEvents();
      this.focusInput();
    }
  }

  private isLoggedIn(): boolean {
    try {
      const loginStatus = localStorage.getItem(this.LOGIN_STATUS_KEY);
      const loginExpiry = localStorage.getItem(this.LOGIN_EXPIRY_KEY);

      if (!loginStatus || !loginExpiry) {
        return false;
      }

      const expiryTime = parseInt(loginExpiry);
      const currentTime = Date.now();

      // Check if login has expired
      if (currentTime > expiryTime) {
        this.clearLoginStatus();
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking login status:", error);
      return false;
    }
  }

  private saveLoginStatus(): void {
    try {
      const expiryTime = Date.now() + this.LOGIN_DURATION;
      localStorage.setItem(this.LOGIN_STATUS_KEY, "true");
      localStorage.setItem(this.LOGIN_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.error("Error saving login status:", error);
    }
  }

  private clearLoginStatus(): void {
    try {
      localStorage.removeItem(this.LOGIN_STATUS_KEY);
      localStorage.removeItem(this.LOGIN_EXPIRY_KEY);
    } catch (error) {
      console.error("Error clearing login status:", error);
    }
  }

  private bindEvents(): void {
    // Submit button click event
    if (this.passwordSubmit) {
      this.passwordSubmit.addEventListener("click", () =>
        this.verifyPassword()
      );
    }

    // Enter key submit
    if (this.passwordInput) {
      this.passwordInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          this.verifyPassword();
        }
      });
    }
  }

  private bindLogoutEvent(): void {
    const logoutBtn = document.getElementById("logoutBtn");
    if (logoutBtn) {
      logoutBtn.addEventListener("click", () => this.logout());
    }
  }

  private logout(): void {
    // Clear login status
    this.clearLoginStatus();

    // Show password interface
    if (this.passwordOverlay) {
      this.passwordOverlay.style.display = "flex";
    }

    // Hide game interface
    if (this.gameContainer) {
      this.gameContainer.style.display = "none";
      this.gameContainer.classList.remove("authenticated");
    }

    // Re-bind password events
    if (this.passwordInput && this.passwordSubmit) {
      this.bindEvents();
      this.focusInput();
    }
  }

  private focusInput(): void {
    if (this.passwordInput) {
      this.passwordInput.focus();
    }
  }

  private verifyPassword(): void {
    const inputPassword = this.passwordInput?.value.trim();

    if (!inputPassword) {
      this.showError("Please enter password");
      return;
    }

    if (inputPassword === this.CORRECT_PASSWORD) {
      this.authenticate();
    } else {
      this.showError("Incorrect password, please try again");
      this.clearInput();
      this.focusInput();
    }
  }

  private showError(message: string): void {
    if (this.passwordError) {
      this.passwordError.textContent = message;
      this.passwordError.classList.add("show");

      // Hide error message after 3 seconds
      setTimeout(() => {
        this.passwordError?.classList.remove("show");
      }, 3000);
    }
  }

  private clearInput(): void {
    if (this.passwordInput) {
      this.passwordInput.value = "";
    }
  }

  private authenticate(): void {
    // Save login status
    this.saveLoginStatus();

    // Hide password interface
    if (this.passwordOverlay) {
      this.passwordOverlay.style.display = "none";
    }

    // Show game interface
    if (this.gameContainer) {
      this.gameContainer.style.display = "block";
      this.gameContainer.classList.add("authenticated");
    }

    // Bind logout event
    this.bindLogoutEvent();

    // Initialize game
    this.initGame();
  }

  private initGame(): void {
    const game = new ETHGame();
    game.init();
  }
}

// Start password verification after page loads
document.addEventListener("DOMContentLoaded", (): void => {
  new PasswordProtection();
});
